import { UIEvent, useRef, useState } from 'react';

import PruDialog from '@/app/common/components/PruDialog';
import PruTable from '@/app/common/components/PruTable/PruTable';
import { CircularProgress, FormControl, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import moment from 'moment';
import { useIntl } from 'react-intl';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useDataProvider } from 'src/app/common/utils/hook-utils';
import { fetchBopReportDownloadRequest, fetchListBopReportRequest } from '../../../network/bopReportCrud';
import PruFilter, { PruFilterItemType } from '../components/Filter';
import { BopReportItem, GpReportPayload, PaginateListMore } from '../types';
import { useStyles } from './styles';
import { fetchLibraryList } from '@/app/modules/ResourceLibrary/network/resourceCrud';
import { RootState } from '@/redux/store';
import { LibraryItem } from '@/app/modules/ResourceLibrary/types';
import { LibraryPublishStatus } from '@/app/modules/ResourceLibrary/pages/SalesMaterial/constants';

const EXPORT_EVENT_PAGE_SIZE = 20;

type EventOption = {
  id: string;
  name: string;
};

const initialState: GpReportPayload = {
  eventName: '',
  eventStartDate: moment().subtract(6, 'months').toISOString(),
  eventEndDate: moment().toISOString(),
  page: 1,
  limit: 20,
};

const paramsInitiator = (initialParams?: GpReportPayload): GpReportPayload => {
  return initialParams ? initialParams : initialState;
};

const BopReport = () => {
  const intl = useIntl();
  const locale = intl.locale;
  const Translation = (id: string) => intl.formatMessage({ id });
  const { classes } = useStyles();

  const dispatch = useDispatch();
  const isFetchingEventOptionsRef = useRef(false);
  const pendingEventOptionsRef = useRef<{ page: number } | null>(null);
  const { officeCodes, roleAdActing, currentUser } = useSelector<RootState, any>((state) => {
    return {
      officeCodes: state.auth.officeCodes,
      roleAdActing: state.selectedRole?.roleAdActing,
      currentUser: state.auth.user,
    };
  }, shallowEqual);

  const [filterState, setFilterState] = useState<GpReportPayload>(paramsInitiator());
  const [reportList, setReportList] = useState<PaginateListMore<BopReportItem>>();
  const [openExport, setOpenExport] = useState(false);
  const [eventOptions, setEventOptions] = useState<EventOption[]>([]);
  const [selectedEventName, setSelectedEventName] = useState<EventOption | null>(null);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [isEventOptionsLoading, setIsEventOptionsLoading] = useState(false);
  const [eventOptionsPage, setEventOptionsPage] = useState(0);
  const [hasMoreEventOptions, setHasMoreEventOptions] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const abortController = useRef(new AbortController());

  const { isLoading, refreshData } = useDataProvider<PaginateListMore<BopReportItem>>(
    async () => {
      try {
        const payload: GpReportPayload = {
          ...filterState,
          ...getReportDateRange(filterState),
          eventName: trimString(filterState.eventName),
          roleAdActing,
        };
        return await fetchListBopReportRequest(payload, dispatch);
      } catch (err) {}
    },
    setReportList,
    false,
  );

  const trimString = (name: string) => {
    const str = name.trim();
    const strReserved = reverseString(str).trim();
    return reverseString(strReserved);
  };

  const reverseString = (str: string) => {
    return str.split('').reverse().join('');
  };

  const getReportDateRange = (data: GpReportPayload) => {
    const { eventEndDate, eventStartDate } = data;
    if (eventEndDate && eventStartDate) {
      return {
        eventEndDate: moment(eventEndDate).endOf('D').toISOString(),
        eventStartDate: moment(eventStartDate).startOf('D').toISOString(),
      };
    }

    if (eventEndDate) {
      return {
        eventEndDate: moment(eventEndDate).endOf('D').toISOString(),
        eventStartDate: moment(eventEndDate).startOf('D').subtract(6, 'months').toISOString(),
      };
    }

    if (eventStartDate) {
      return {
        eventStartDate: moment(eventStartDate).startOf('D').toISOString(),
        eventEndDate: moment(eventStartDate).endOf('D').add(6, 'months').toISOString(),
      };
    }

    return {
      eventEndDate: moment().endOf('D').toISOString(),
      eventStartDate: moment().startOf('D').subtract(6, 'months').toISOString(),
    };
  };

  const mapEventOptions = (items: LibraryItem[]): EventOption[] => {
    return items.map((item) => ({
      id: item.id,
      name: item.name,
    }));
  };

  const mergeEventOptions = (currentOptions: EventOption[], nextOptions: EventOption[]) => {
    const optionMap = new Map(currentOptions.map((item) => [item.id, item]));

    for (const option of nextOptions) {
      optionMap.set(option.id, option);
    }

    return Array.from(optionMap.values());
  };

  const fetchEventOptions = async (page = 1) => {
    if (isFetchingEventOptionsRef.current) {
      pendingEventOptionsRef.current = { page };
      return;
    }

    isFetchingEventOptionsRef.current = true;

    setIsFirstLoad(true);
    const signal = abortController.current.signal;
    const eventStartDate = moment().subtract(6, 'months').startOf('D').toISOString();
    const eventEndDate = moment().endOf('D').toISOString();

    const payload = {
      roleAdActing,
      lastUpdatedBySub: currentUser.sub,
      eventStartDate,
      eventEndDate,
      page,
      pageSize: EXPORT_EVENT_PAGE_SIZE,
      module: 'Recruit',
      officeCodes,
      customizeQueryBuilder: () => {
        return {
          status: {
            $ne: LibraryPublishStatus.Draft,
          },
        };
      },
    };
    try {
      const response = await fetchLibraryList(locale, payload, [], dispatch, signal);

      const nextOptions = mapEventOptions(response?.libraryList || []);
      const resolvedPage = page;
      const resolvedHasMore =
        typeof response?.hasNextPage === 'boolean'
          ? response.hasNextPage
          : Boolean(response?.totalPages) && resolvedPage < response.totalPages;

      setEventOptions((currentOptions) => (page === 1 ? nextOptions : mergeEventOptions(currentOptions, nextOptions)));
      setEventOptionsPage(resolvedPage);
      setHasMoreEventOptions(resolvedHasMore);
    } catch (err) {
      if (page === 1) {
        setEventOptions([]);
      }
      setHasMoreEventOptions(false);
    } finally {
      isFetchingEventOptionsRef.current = false;
      setIsEventOptionsLoading(false);
      setIsFirstLoad(false);

      if (pendingEventOptionsRef.current) {
        const pending = pendingEventOptionsRef.current;
        pendingEventOptionsRef.current = null;
        await fetchEventOptions(pending.page);
      }
    }
  };

  const openExportDialog = async () => {
    setEventOptions([]);
    setEventOptionsPage(0);
    setHasMoreEventOptions(true);
    setSelectedEventName(null);
    setOpenExport(true);
    await fetchEventOptions(1);
  };

  const closeDialog = () => {
    pendingEventOptionsRef.current = null;
    setOpenExport(false);
    setEventOptions([]);
    setEventOptionsPage(0);
    setHasMoreEventOptions(true);
    setSelectedEventName(null);
  };

  const onScrollEventOptions = async (event: UIEvent<HTMLElement>) => {
    const listboxNode = event.currentTarget;
    const isReachedBottom = listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 20;

    if (!isReachedBottom || isFetchingEventOptionsRef.current || !hasMoreEventOptions) {
      return;
    }

    await fetchEventOptions(eventOptionsPage + 1);
  };

  const onChangeSelectedEvent = (event: SelectChangeEvent<string>) => {
    const selectedId = event.target.value;
    const selectedOption = eventOptions.find((option) => option.id === selectedId) || null;
    setSelectedEventName(selectedOption);
  };

  const onSubmit = async () => {
    if (!selectedEventName) {
      return;
    }

    setIsExporting(true);

    try {
      await fetchBopReportDownloadRequest(
        {
          id: selectedEventName?.id,
        },
        dispatch,
      );
      closeDialog();
    } catch (err) {
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <PruFilter
        title={Translation('report.bop-report.filter')}
        itemDef={[
          {
            type: PruFilterItemType.FREE_TEXT,
            field: 'eventName',
            initialValue: filterState.eventName,
            displayName: Translation('report.event.event-name'),
            style: { width: '100%' },
          },

          {
            type: PruFilterItemType.DATE_RANGE,
            fieldFrom: 'eventStartDate',
            fieldTo: 'eventEndDate',
            initialDateFrom: filterState.eventStartDate
              ? new Date(filterState.eventStartDate)
              : moment().subtract(6, 'months').toDate(),
            initialDateTo: filterState.eventEndDate ? new Date(filterState.eventEndDate) : moment().toDate(),
            defaultDateFrom: moment().subtract(6, 'months').toDate(),
            defaultDateTo: moment().toDate(),
            displayName: Translation('report.event.event-date'),
            style: { width: '100%' },
          },
        ]}
        onChangeFilter={(data) =>
          setFilterState({
            ...filterState,
            eventName: data.eventName,
            eventStartDate: data.eventStartDate,
            eventEndDate: data.eventEndDate,
          })
        }
        fetchData={refreshData}
      />
      <PruTable
        disableBulkSelect
        dataSource={reportList?.items}
        isLoading={isLoading}
        onRefresh={refreshData}
        title={Translation('report.bop-report.bop-event')}
        headerBtnDef={[
          {
            color: 'inherit',
            title: Translation('report.common.export'),
            onClick: openExportDialog,
            className: classes.btnBlue,
          },
        ]}
        operationDef={[]}
        columnHeaderStyle={{
          backgroundColor: 'black',
          color: 'white',
          fontWeight: 'bold',
        }}
        columnDef={[
          {
            isId: true,
            hidden: true,
            keyIndex: '_id',
            displayName: '',
            renderData: (row) => row._id,
          },
          {
            keyIndex: 'bdm',
            displayName: Translation('report.bop-report.bdm'),
            renderData: (row) => row.bdm || '-',
            width: 200,
          },
          {
            keyIndex: 'officeCode',
            displayName: Translation('report.bop-report.officeCode'),
            renderData: (row) => row.officeCode || '-',
            width: 120,
          },
          {
            keyIndex: 'branchCode',
            displayName: Translation('report.bop-report.branchCode'),
            renderData: (row) => row.branchCode || '-',
            width: 120,
          },
          {
            keyIndex: 'unitCode',
            displayName: Translation('report.bop-report.unitCode'),
            renderData: (row) => row.unitCode || '-',
            width: 120,
          },
          {
            keyIndex: 'sourceId',
            displayName: 'Source ID',
            renderData: (row) => row.sourceId || '-',
          },
          {
            keyIndex: 'eventName',
            displayName: Translation('report.bop-report.eventName'),
            renderData: (row) => row.eventName || '-',
            width: 200,
          },
          {
            keyIndex: 'fullName',
            displayName: Translation('report.bop-report.candidate-name'),
            renderData: (row) => row.fullName || '-',
            width: 200,
          },
          {
            keyIndex: 'phoneNumber',
            displayName: Translation('report.bop-report.phoneNumber'),
            renderData: (row) => row.phoneNumber || '-',
            width: 150,
          },
          {
            keyIndex: 'agentCode',
            displayName: Translation('report.bop-report.agentCode'),
            renderData: (row) => row.agentCode || '-',
            width: 120,
          },
          {
            keyIndex: 'agentName',
            displayName: Translation('report.bop-report.agentName'),
            renderData: (row) => row.agentName || '-',
            width: 200,
          },
          {
            keyIndex: 'leaderCode',
            displayName: Translation('report.bop-report.leaderCode'),
            renderData: (row) => row.leaderCode || '-',
            width: 120,
          },
          {
            keyIndex: 'leaderName',
            displayName: Translation('report.bop-report.leaderName'),
            renderData: (row) => row.leaderName || '-',
            width: 200,
          },
          {
            keyIndex: 'materialEventStartDate',
            displayName: Translation('report.bop-report.materialEventStartDate'),
            renderData: (row) =>
              row.materialEventStartDate ? moment(row.materialEventStartDate).format('DD/MM/YYYY HH:mm') : '-',
            width: 160,
          },
          {
            keyIndex: 'materialEventEndDate',
            displayName: Translation('report.bop-report.materialEventEndDate'),
            renderData: (row) =>
              row.materialEventStartDate ? moment(row.materialEventEndDate).format('DD/MM/YYYY HH:mm') : '-',
            width: 160,
          },
          {
            keyIndex: 'registeredDate',
            displayName: Translation('report.bop-report.registeredDate'),
            renderData: (row) => (row.registeredDate ? moment(row.registeredDate).format('DD/MM/YYYY HH:mm') : '-'),
            width: 160,
          },
          {
            keyIndex: 'checkInDate',
            displayName: Translation('report.bop-report.checkInDate'),
            renderData: (row) => (row.checkInDate ? moment(row.checkInDate).format('DD/MM/YYYY HH:mm') : '-'),
            width: 160,
          },
        ]}
        totalPages={reportList?.totalPages}
        totalRecords={reportList?.totalRecords}
        onChangePage={(page, rowsPerPage) => {
          setFilterState({
            ...filterState,
            page,
            limit: rowsPerPage,
          });
          refreshData();
        }}
      />

      <PruDialog
        open={openExport}
        canCloseDialog={!isExporting}
        onCancel={closeDialog}
        onOk={onSubmit}
        dialogTitle={Translation('report.bop-report.export-dialog.title')}
        confirmBtnText={Translation('report.common.download')}
        canncelBtnText={Translation('app.button.cancel')}
        disabledConfirm={!selectedEventName || isEventOptionsLoading || isExporting}
        loading={isExporting}
      >
        <div style={{ width: '480px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <FormControl size="small" fullWidth>
            <Select
              value={selectedEventName?.id || ''}
              onChange={onChangeSelectedEvent}
              displayEmpty
              renderValue={(selected) => {
                if (!selected) {
                  return Translation('report.event.event-name');
                }

                return selectedEventName?.name || '';
              }}
              MenuProps={{
                PaperProps: {
                  onScroll: onScrollEventOptions,
                  style: {
                    maxHeight: 320,
                  },
                },
                MenuListProps: {
                  onScroll: onScrollEventOptions,
                },
              }}
              endAdornment={isEventOptionsLoading ? <CircularProgress color="inherit" size={16} /> : null}
            >
              {!isFirstLoad &&
                (eventOptions.length ? (
                  eventOptions.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.name}
                    </MenuItem>
                  ))
                ) : (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '16px',
                    }}
                  >
                    No data
                  </div>
                ))}
              {((isEventOptionsLoading && Boolean(eventOptions.length)) || isFirstLoad) && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '8px 16px',
                  }}
                >
                  ...loading
                </div>
              )}
            </Select>
          </FormControl>
        </div>
      </PruDialog>
    </>
  );
};

export default BopReport;
